// Theme (DESIGN §2.1): `<html data-theme="dark|light">` ist die einzige Quelle. Die Einstellung
// (dark · light · system) liegt in localStorage['bar.theme'] und im Konto (Rückruf
// `onPreferenceChange`); `?theme=` gilt nur für die Sitzung (sessionStorage['bar.theme.session']).
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ThemePreference = 'dark' | 'light' | 'system';
export type Theme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'bar.theme';
export const THEME_SESSION_KEY = 'bar.theme.session';
const LIGHT_QUERY = '(prefers-color-scheme: light)';

const isPreference = (v: unknown): v is ThemePreference =>
  v === 'dark' || v === 'light' || v === 'system';
const isTheme = (v: unknown): v is Theme => v === 'dark' || v === 'light';

function safeGet(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function safeSet(storage: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    // Speicher gesperrt: Einstellung gilt nur bis zum Neuladen.
  }
}

export function readStoredPreference(): ThemePreference {
  const stored = safeGet(() => localStorage, THEME_STORAGE_KEY);
  return isPreference(stored) ? stored : 'dark';
}

/** Sitzungs-Override aus `?theme=` (vom Head-Skript gesetzt) oder null. */
export function readSessionTheme(): Theme | null {
  const session = safeGet(() => sessionStorage, THEME_SESSION_KEY);
  return isTheme(session) ? session : null;
}

export function systemTheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'dark';
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

export function resolveTheme(preference: ThemePreference): Theme {
  return preference === 'system' ? systemTheme() : preference;
}

/** Setzt `data-theme` und `color-scheme`, ohne dass Übergänge nachziehen (ein Frame ohne transition). */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): void {
  if (root.getAttribute('data-theme') === theme) return;
  root.setAttribute('data-theme-switching', '');
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme;
  const done = () => root.removeAttribute('data-theme-switching');
  if (typeof requestAnimationFrame === 'function')
    requestAnimationFrame(() => requestAnimationFrame(done));
  else done();
}

/**
 * Inline-Skript für den `<head>` (vor dem ersten Paint). Gleiche Regel wie apps/web/theme-boot.js;
 * für Clients ohne Vite-Plugin (Erweiterung, Mac-App).
 */
export const themeBootstrap = `(function(){var t='dark';try{var q=new URLSearchParams(location.search).get('theme');var s=sessionStorage.getItem('${THEME_SESSION_KEY}');var p=localStorage.getItem('${THEME_STORAGE_KEY}');if(q==='dark'||q==='light'){sessionStorage.setItem('${THEME_SESSION_KEY}',q);t=q}else if(s==='dark'||s==='light'){t=s}else if(p==='dark'||p==='light'){t=p}else if(p==='system'){t=matchMedia('${LIGHT_QUERY}').matches?'light':'dark'}}catch(e){}document.documentElement.setAttribute('data-theme',t);document.documentElement.style.colorScheme=t})();`;

interface ThemeContextValue {
  /** Gewählte Einstellung (ohne Sitzungs-Override). */
  preference: ThemePreference;
  /** Tatsächlich angezeigtes Theme. */
  theme: Theme;
  /** true, solange `?theme=` die Einstellung für diese Sitzung überschreibt. */
  sessionOverride: boolean;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  /** Startwert, wenn nichts gespeichert ist (Konto-Einstellung vom Server). */
  initialPreference?: ThemePreference;
  /** Wird nach jeder Wahl aufgerufen (Konto-Spiegel, F-konten-10). */
  onPreferenceChange?: (preference: ThemePreference) => void;
}

export function ThemeProvider({
  children,
  initialPreference,
  onPreferenceChange,
}: ThemeProviderProps) {
  const [preference, setPref] = useState<ThemePreference>(() => {
    const stored = safeGet(() => localStorage, THEME_STORAGE_KEY);
    return isPreference(stored) ? stored : (initialPreference ?? 'dark');
  });
  const [session, setSession] = useState<Theme | null>(() => readSessionTheme());
  const [system, setSystem] = useState<Theme>(() => systemTheme());

  useEffect(() => {
    if (preference !== 'system' || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(LIGHT_QUERY);
    const update = () => setSystem(media.matches ? 'light' : 'dark');
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [preference]);

  const theme: Theme = session ?? (preference === 'system' ? system : preference);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      safeSet(() => localStorage, THEME_STORAGE_KEY, next);
      safeSet(() => sessionStorage, THEME_SESSION_KEY, null); // ?theme= gilt bis zur nächsten Wahl
      setSession(null);
      setPref(next);
      onPreferenceChange?.(next);
    },
    [onPreferenceChange],
  );

  const sessionOverride = session !== null;
  const value = useMemo(
    () => ({ preference, theme, sessionOverride, setPreference }),
    [preference, theme, sessionOverride, setPreference],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Theme lesen und wählen. Außerhalb eines ThemeProviders: nur lesen (Galerie-Tests, Clients). */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value) return value;
  const theme: Theme =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-theme') === 'light'
      ? 'light'
      : 'dark';
  return { preference: theme, theme, sessionOverride: false, setPreference: () => undefined };
}
